<template>
  <transition
    name="fade"
    mode="out-in"
    @beforeLeave="beforeLeave"
    @enter="enter"
    @afterEnter="afterEnter"
  >
    <section
      ref="provider-statement-authenticate"
      class="provider-statement-authenticate"
      v-show="isShown"
    >
      <div class="provider-statement-authenticate_form">
        <h2
          v-if="this.fromguid == false"
          class="provider-statement-authenticate_form_title"
          tabindex="-1"
        >
          Now, let's find your statement balance.
        </h2>
        <h2
          v-else
          class="provider-statement-authenticate_form_title"
          tabindex="-1"
        >
          Let's find your statement balance.
        </h2>
        <p class="provider-statement-authenticate_form_description">
          Together with {{ selectedProvider.providerName }}, we have your most
          current information available to make applying for an account easier.
          We just need a little information to get started.
        </p>
        <div class="provider-statement-authenticate_form_guidance">
          <h3>Locating Your {{ selectedProvider.customAcctIdFieldName ? selectedProvider.customAcctIdFieldName : 'Account Number' }}</h3>
          <span>You may locate the {{ selectedProvider.customAcctIdFieldName ? selectedProvider.customAcctIdFieldName : 'Account Number' }} on your billing statement.</span>
        </div>
        <img v-if="getBillingStatementImage && getBillingStatementImage !== ''" class="preview-img" :src="getBillingStatementImage" />
        <ErrorBox
          v-if="fileMethodError"
          title="Error"
          errors="We are unable to authenticate you with the information provided.  Please check information provided and try again, or click the link to continue without authenticating."
          class="error-box"
        />
        <div class="vcore_row" ref="containerRef">
          <CustomForm
            :ref="activeForms[0]"
            :form="formData.authentication"
            :value="formValues.authentication"
            @input="handleCustomFormInput('authentication', $event)"
            @valid-change="handleValidChange(activeForms[0], $event)"
            @errors-change="handleErrorsChange(activeForms[0], $event)"
          />
        </div>
        <div class="provider-statement-authenticate_actions vcore_row">
          <div class="vcore_row">
            <div v-if="selectedProvider.showSelfEnrActionText" class="vcore_col--md--4 vcore_col--lg--3">
              <Button
                id="no_auth_btn"
                size="small"
                color="blue"
                variant="secondary"
                classes="hover_blue focus_blue"
                @click.native="handleNonAuthClick"
                block
              >
                Don’t have your account number?<br />Continue without
                authenticating.
              </Button>
            </div>
            <div
              class="provider-statement-authenticate_action_auth vcore_col--xs--12 vcore_col--sm--5 vcore_col--md--4 vcore_col--lg--3"
            >
              <Button
                size="large"
                color="blue"
                block
                @click.native="handleEnrollClick"
              >
                Enroll
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  </transition>
</template>

<script>
import Vue from "vue";
import Uuid from "@Shared/utils/Uuid";
import Form from "@Shared/mixins/Form";
import { mapGetters, mapActions } from "vuex";
import Button from "@Shared/components/Button";
import ErrorBox from "@Shared/components/ErrorBox";
import EnrollmentAuthForm from "../../forms/EnrollmentAuth.json";
import { EventBus } from "@Shared/utils/event-bus";
import { KEYS, getCustomBrandingByKey } from "@Shared/utils/CustomBranding";

export default {
  name: "ProviderStatementAuthenticate",
  mixins: [Form],
  components: {
    ErrorBox,
    Button,
  },
  data() {
    return {
      formData: {
        authentication: EnrollmentAuthForm,
      },
      id: "vertical-progress-flow-" + Uuid(),
      stepIndex: 2, // 0 count, must match component order in slot
      activeForms: ["authForm"],
      containerObserver: null,
    };
  },
  props: {
    fromguid: {
      // required: true,
      type: Boolean,
    },
  },
  computed: {
    ...mapGetters([
      "enrollmentCurrentSearchStepIndex",
      "selectedProvider",
      "activeErrors",
      "fileMethodError",
      "fileMethodUserID",
      "attempts",
      "getEnrollmentSource",
      "getBillingStatementImage",
    ]),
    isShown() {
      if (!this.fromguid) {
        return this.stepIndex <= this.enrollmentCurrentSearchStepIndex;
      } else {
        return true;
      }
    },
  },
  watch: {
    isShown() {
      this.$nextTick((val) => {
        this.updateOffsetTop();

        if (val) {
          this.$refs.sectionTitle.focus();
        }
      });
    },
  },
  methods: {
    ...mapActions([
      "incrementEnrollmentCurrentStepIndex",
      "fileMethodAuth",
      "resetForms",
      "fetchBillingStatementImage"
    ]),

    handleEnrollClick() {
      this.$refs.authForm.touch();

      // Run on next tick to allow for touch to finish setting everything.
      this.$nextTick(() => {
        if (this.activeErrors.length === 0) {
          const spid = this.selectedProvider.spid;
          const providerId = this.selectedProvider.guid;
          const revspring = this.$router.currentRoute.query.source === "revspring";
          this.fileMethodAuth()
            .then(() => {
              if(revspring){
                window.location = `${process.env.VUE_APP_INTENROLL_PATH}?user=${this.fileMethodUserID}&spid=${spid}&providerId=${providerId}&source=revspring`;
              }
              else{
                window.location = `${process.env.VUE_APP_INTENROLL_PATH}?user=${this.fileMethodUserID}&spid=${spid}&providerId=${providerId}`;
              }
              
            })
            .catch((err) => {
              console.error(err);
              this.$emit("major-error");
              this.$store.commit("SET_FILE_METHOD_ERROR", true);
            });
        }
      });
    },
    handleNonAuthClick() {
      this.incrementEnrollmentCurrentStepIndex();
      this.$router
        .push({
          name: "enroll-forms",
          params: { step: "frp-information" },
        })
        .catch(() => {});
      this.$ga.event({
        eventCategory: "Provider Enrollment",
        eventAction: "Button Click",
        eventLabel: "Auth",
      });
    },
    updateOffsetTop() {
      Vue.set(
        this.verticalProgressSteps[this.stepIndex],
        "offsetTop",
        this.$refs["provider-statement-authenticate"].offsetTop
      );
    },
    beforeLeave(element) {
      this.prevHeight = getComputedStyle(element).height;
      this.$refs.authForm.reset();
      this.resetForms();
    },
    enter(element) {
      const { height } = getComputedStyle(element);
      element.style.height = this.prevHeight;
      setTimeout(() => {
        element.style.height = height;
      });
    },
    afterEnter(element) {
      element.style.height = "auto";
    },
    statementImg() {
      return getCustomBrandingByKey(
        KEYS.STATEMENT_IMG,
        this.selectedProvider.customBrandingList
      );
    },
  },
  inject: ["verticalProgressSteps"],
  mounted() {
    if (!this.fromguid) {
      const elements = document.getElementsByClassName("provider-selection-item-grid");
      let lastElement = elements[0];
      this.verticalProgressSteps.push({
        id: this.id,
        stepIndex: this.stepIndex,
        offsetTop: lastElement.offsetTop + lastElement.offsetHeight + 400,
      });
    }
    this.$nextTick(function () {
      const dobLabel = document.getElementById('date-of-birth--label');
      const tinLabel = document.getElementById('social-security-number--label');
      const acctNumLabel = document.getElementById('account-number--label');
      if (dobLabel && this.selectedProvider.customDOBFieldName) {
        dobLabel.textContent = this.selectedProvider.customDOBFieldName;
      }
      if (tinLabel && this.selectedProvider.customSSNFieldName) {
        tinLabel.textContent = this.selectedProvider.customSSNFieldName;
      }
      if (acctNumLabel && this.selectedProvider.customAcctIdFieldName) {
        acctNumLabel.textContent = this.selectedProvider.customAcctIdFieldName;
      }
    });
    this.$nextTick(function () {
      var elemPAN = document.getElementById("popover_ssn");
      window.myPAN = elemPAN;
      if (elemPAN && elemPAN.parentElement) {
        var elemTooltip = elemPAN.parentElement.querySelector(
          ".vcore_popover-content"
        );
        window.myTooltip = elemTooltip;
        if (this.selectedProvider.customSSNFieldName && elemTooltip.textContent) {
          elemTooltip.textContent = elemTooltip.textContent.replace("Social Security Number", this.selectedProvider.customSSNFieldName);
        }
      }
    });
    this.$nextTick(function () {
      var elemPAN = document.getElementById("popover-account-number");
      window.myPAN = elemPAN;
      if (elemPAN && elemPAN.parentElement) {
        var elemTooltip = elemPAN.parentElement.querySelector(
          ".vcore_popover-content"
        );
        window.myTooltip = elemTooltip;
        if (this.selectedProvider.customAcctIdFieldName && elemTooltip.textContent) {
          elemTooltip.textContent = elemTooltip.textContent.replace("account number", this.selectedProvider.customAcctIdFieldName);
        }
        var statementImg = this.statementImg();
        if (statementImg !== null && statementImg !== "") {
          var elemImg = document.createElement("img");
          elemPAN.setAttribute("id", "toolTipBox");
          elemTooltip.setAttribute("id", "toolTipContent");
          elemImg.setAttribute("id", "toolTipImage");
          elemImg.src = statementImg;
          elemTooltip.appendChild(elemImg);
        }
      }
    });
    if (!this.fromguid) {
      EventBus.$on("window-resize", this.updateOffsetTop);
      EventBus.$on("update-offset", this.updateOffsetTop);
    }

    this.fetchBillingStatementImage({
      spid: this.selectedProvider.spid,
    });

    if (this.$refs.containerRef) {
      this.containerObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.addedNodes.length > 0) {
            for (const node of mutation.addedNodes) {
              if (node.nodeType === Node.ELEMENT_NODE && node.textContent) {
                if (node.id === "date-of-birth--error" && this.selectedProvider.customDOBFieldName) {
                  node.textContent = node.textContent.replace("Date of Birth", this.selectedProvider.customDOBFieldName);
                }
                if (node.id === "social-security-number--error" && this.selectedProvider.customSSNFieldName) {
                  node.textContent = node.textContent.replace("Social Security Number", this.selectedProvider.customSSNFieldName);
                }
                if (node.id === "account-number--error" && this.selectedProvider.customAcctIdFieldName) {
                  node.textContent = node.textContent.replace("account number", this.selectedProvider.customAcctIdFieldName);
                }
              }
            }
          }
        });
      });
      this.containerObserver.observe(this.$refs.containerRef, {
        childList: true,
        subtree: true,
      });
    }
  },
  beforeDestroy() {
    if (this.containerObserver) {
      this.containerObserver.disconnect();
      this.containerObserver = null;
    }
    this.$refs.authForm.reset();
    this.resetForms();
  },
  destroyed() {
    if (!this.fromguid) {
      EventBus.$off("window-resize", this.updateOffsetTop);
      EventBus.$off("update-offset", this.updateOffsetTop);
    }
  },
};
</script>

<style lang="scss">
#toolTipContent {
  width: 100%;
}

#toolTipBox {
  width: 100%;
}

#toolTipImage {
  width: 100%;
}
</style>

<style lang="scss" scoped>
.provider-statement-authenticate {
  margin-top: 35px;

  p, span {
    font-family: 'Open Sans', sans-serif;
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: .03em;
  }
  
  .provider-statement-authenticate_form {
    background-color: $color_gray-7;
    padding: 60px 38px;
    border-radius: 10px;

    .provider-statement-authenticate_form_title {
      font-size: 24px;
      font-weight: 600;
    }

    .provider-statement-authenticate_form_description {
      margin-top: 15px;
    }

    .provider-statement-authenticate_form_guidance {
      display: flex;
      flex-direction: column;
      gap: 8px;
      align-items: flex-start;
      margin-top: 33px;

      h3 {
        font-family: 'Poppins';
        font-weight: 600;
        font-size: 16px;
        line-height: 24px;
        letter-spacing: .02em;
      }
    }
  }

  .preview-img {
    margin-top: 33px;
    max-width: 100%;
  }

  .provider-statement-authenticate_actions {
    margin-top: 30px;
  }

  .provider-statement-authenticate_action_no-auth {
    font-size: 14px;
    font-weight: bold;
    color: $color_dark-blue;
    text-decoration: none;

    > div {
      padding: 4px;
    }

    @media (max-width: $grid-breakpoint-sm) {
      text-align: center;
    }
  }

  #no_auth_btn {
    float: left;
    height: 100%;
    width: 250px;
    line-height: 25px;
    align-items: normal;
    text-align: left;
    padding-left: 5px;
    padding-right: 5px;
    border-style: none;
    font-weight: bold;
    background-color: #f7f9fa;
  }

  .provider-statement-authenticate_action_auth {
    float: right;

    @media (max-width: $grid-breakpoint-sm) {
      margin-top: 20px;
    }
  }
}
.error-box {
  margin-top: 15px;
}

.fade-enter-active,
.fade-leave-active {
  transition-duration: 0.5s;
  transition-property: height, opacity;
  transition-timing-function: ease;
  overflow: hidden;
}
.fade-enter,
.fade-leave-active {
  opacity: 0;
}
</style>
